import React from 'react'

/* ═══════════════════════════════════════════════════════════════
   MobileCard — Reusable card component for mobile data display
   Replaces tables on mobile with touch-friendly card views.
   ═══════════════════════════════════════════════════════════════ */

interface MobileCardField {
  label: string
  value: React.ReactNode
  accent?: 'green' | 'red' | 'yellow' | 'muted'
  bold?: boolean
}

interface MobileCardProps {
  /** Top-left title */
  title: string
  /** Optional subtitle below title */
  subtitle?: string
  /** Status badge — top-right */
  status?: {
    label: string
    variant: 'green' | 'red' | 'yellow' | 'blue' | 'muted'
  }
  /** Key-value fields displayed in a 2-col grid */
  fields: MobileCardField[]
  /** Action buttons at bottom */
  actions?: {
    label: string
    icon?: string
    variant?: 'primary' | 'danger' | 'ghost'
    onClick: () => void
  }[]
  /** Click handler for the whole card */
  onClick?: () => void
  /** Extra className */
  className?: string
}

const accentColor: Record<string, string> = {
  green: 'var(--green)',
  red: 'var(--red)',
  yellow: '#f59e0b',
  blue: 'var(--accent)',
  muted: 'var(--text-muted)',
}

export function MobileCard({ title, subtitle, status, fields, actions, onClick, className }: MobileCardProps) {
  return (
    <div
      className={`m-card ${className || ''}`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      {/* Header row */}
      <div className="m-card-head">
        <div className="m-card-title-area">
          <span className="m-card-title">{title}</span>
          {subtitle && <span className="m-card-subtitle">{subtitle}</span>}
        </div>
        {status && (
          <span className="m-card-badge" style={{ background: `${accentColor[status.variant]}22`, color: accentColor[status.variant], border: `1px solid ${accentColor[status.variant]}44` }}>
            {status.label}
          </span>
        )}
      </div>

      {/* Fields grid */}
      {fields.length > 0 && (
        <div className="m-card-fields">
          {fields.map((f, i) => (
            <div className="m-card-field" key={i}>
              <span className="m-card-label">{f.label}</span>
              <span className="m-card-value" style={{
                color: f.accent ? accentColor[f.accent] : undefined,
                fontWeight: f.bold ? 700 : undefined,
              }}>{f.value}</span>
            </div>
          ))}
        </div>
      )}

      {/* Actions */}
      {actions && actions.length > 0 && (
        <div className="m-card-actions">
          {actions.map((a, i) => (
            <button
              key={i}
              className={`m-card-btn ${a.variant || 'ghost'}`}
              onClick={(e) => { e.stopPropagation(); a.onClick() }}
            >
              {a.icon && <span>{a.icon}</span>}
              {a.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

/* ─── Mobile-only / Desktop-only wrappers ─── */

export function MobileOnly({ children }: { children: React.ReactNode }) {
  return <div className="mobile-only">{children}</div>
}

export function DesktopOnly({ children }: { children: React.ReactNode }) {
  return <div className="desktop-only">{children}</div>
}

/* ─── Mobile KPI Card ─── */

interface MobileKpiProps {
  label: string
  value: string
  subtitle?: string
  accent?: 'green' | 'red' | 'yellow' | 'muted'
  icon?: string
}

export function MobileKpi({ label, value, subtitle, accent, icon }: MobileKpiProps) {
  return (
    <div className="m-kpi">
      <div className="m-kpi-top">
        {icon && <span className="m-kpi-icon">{icon}</span>}
        <span className="m-kpi-label">{label}</span>
      </div>
      <div className="m-kpi-value" style={{ color: accent ? accentColor[accent] : undefined }}>
        {value}
      </div>
      {subtitle && <div className="m-kpi-sub">{subtitle}</div>}
    </div>
  )
}
